import React from 'react';
import { CheckCircle, XCircle, Loader, ArrowRight } from 'lucide-react';

/**
 * Compact call status badge for the Telecaller UI.
 *
 * Renders: [provider] [status] – [reason]
 * Shows fallback chain when the gateway tried multiple providers.
 *
 * Props:
 *   state: {
 *     status: 'calling' | 'success' | 'failed' | 'idle',
 *     statusText?: string,
 *     reason?: string,
 *     providerLabel?: string,
 *     attempts?: Array<{providerLabel, ok, reason}>,
 *     fellBackFrom?: string | null,
 *   }
 *   compact?: boolean
 */
export default function CallStatusBadge({ state, compact = false }) {
  if (!state || state.status === 'idle') return null;

  const isSuccess = state.status === 'success';
  const isFailed = state.status === 'failed';
  const isCalling = state.status === 'calling';

  const tone = isSuccess
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : isFailed
      ? 'bg-rose-50 border-rose-200 text-rose-800'
      : 'bg-amber-50 border-amber-200 text-amber-800';

  const Icon = isSuccess ? CheckCircle : isFailed ? XCircle : Loader;

  const provider = state.providerLabel || '';
  const word = state.statusText || state.status;

  const attempts = Array.isArray(state.attempts) ? state.attempts : [];

  return (
    <div className={`mt-2 rounded-lg border px-3 py-2 text-xs ${tone}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Icon size={14} className={isCalling ? 'animate-spin' : ''} />
        <span className="font-bold uppercase tracking-wider">Call Status</span>
        <span className="text-gray-400">·</span>
        {provider ? (
          <span className="font-semibold">{provider}</span>
        ) : null}
        <span className="px-2 py-0.5 rounded-full bg-white/70 border border-current/20 font-bold uppercase tracking-wide">
          {word}
        </span>
        {state.fellBackFrom ? (
          <span className="text-[11px] text-gray-600 italic">
            (failover from {state.fellBackFrom})
          </span>
        ) : null}
      </div>

      {isFailed && state.reason ? (
        <div className="mt-1 text-[12px] leading-snug">{state.reason}</div>
      ) : null}

      {!compact && attempts.length > 1 ? (
        <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-gray-700">
          {attempts.map((a, i) => (
            <React.Fragment key={`${a.providerLabel}-${i}`}>
              {i > 0 ? <ArrowRight size={11} className="text-gray-400" /> : null}
              <span
                className={`px-1.5 py-0.5 rounded ${
                  a.ok ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                }`}
                title={a.reason || ''}
              >
                {a.providerLabel} {a.ok ? '✓' : '✕'}
              </span>
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </div>
  );
}
